/**
 * @fileoverview Search results component.
 * @summary Displays song search results with add to queue action.
 *
 * @package KaraoNow
 * @module apps/front/src/pages/singer/components/SearchResults
 * @author Pierre-Yves Léglise <contact@axialdata.net>
 * @license EULA
 */

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faPlus, faSpinner, faSearch } from '@fortawesome/free-solid-svg-icons'
import { useTranslation } from 'react-i18next'
import type { FC } from 'react'
import type { Song } from '../../../store/slices/songsSlice'

interface SearchResultsProps {
  results: Song[]
  isSearching: boolean
  isSearching_outer?: boolean
  searchError?: string | null
  canLoadMore: boolean
  isLoadingMore: boolean
  hasSearched: boolean
  addingToQueue: string | null
  onAddToQueue: (song: Song) => void
  onLoadMore: () => void
  /** When search returns no results, callback to open "add custom song" modal. */
  onAddCustomClick?: () => void
}

/**
 * @summary Search results component.
 * @param {SearchResultsProps} props - Component props.
 * @returns {ReactElement} Search results component.
 */
const SearchResults: FC<SearchResultsProps> = ({
  results,
  isSearching,
  searchError,
  canLoadMore,
  isLoadingMore,
  hasSearched,
  addingToQueue,
  onAddToQueue,
  onLoadMore,
  onAddCustomClick,
}) => {
  const { t } = useTranslation()

  // Show error
  if (searchError) {
    return (
      <div className="alert alert-error mt-4">
        <span>{searchError}</span>
      </div>
    )
  }

  // Show results
  if (results.length > 0) {
    return (
      <>
        <div className="mt-4 space-y-2 max-h-80 overflow-y-auto">
          {results.map((song) => (
            <div
              key={song.id}
              className="flex items-center justify-between p-3 bg-(--color-bg-secondary) rounded-lg hover:bg-(--color-bg-tertiary) transition-colors"
            >
              <div className="flex-1 min-w-0">
                <div className="font-medium text-(--color-text-primary) truncate">{song.title}</div>
                <div className="text-sm text-(--color-text-secondary) truncate">
                  {song.artist}
                </div>
              </div>
              <button
                onClick={() => onAddToQueue(song)}
                className="btn btn-primary btn-sm ml-2 shrink-0"
                disabled={addingToQueue === song.id}
              >
                {addingToQueue === song.id ? (
                  <FontAwesomeIcon icon={faSpinner} className="animate-spin" />
                ) : (
                  <FontAwesomeIcon icon={faPlus} />
                )}
              </button>
            </div>
          ))}
        </div>

        {/* Load more button */}
        {canLoadMore && (
          <button
            onClick={onLoadMore}
            className="w-full mt-3 py-2 px-4 bg-(--color-bg-secondary) hover:bg-(--color-bg-tertiary) text-(--color-text-primary) rounded-lg transition-colors flex items-center justify-center gap-2"
            disabled={isLoadingMore}
          >
            {isLoadingMore ? (
              <>
                <FontAwesomeIcon icon={faSpinner} className="animate-spin" />
                {t('common.loading')}
              </>
            ) : (
              <>
                <FontAwesomeIcon icon={faSearch} />
                {t('singer.moreResults')}
              </>
            )}
          </button>
        )}
      </>
    )
  }

  // No results message + option to add custom song
  if (hasSearched && !isSearching) {
    return (
      <div className="mt-4 space-y-3 text-center">
        <p className="text-(--color-text-secondary)">
          {t('singer.noResults')}
        </p>
        {onAddCustomClick && (
          <button
            type="button"
            onClick={onAddCustomClick}
            className="btn btn-primary btn-sm inline-flex items-center gap-2"
          >
            <FontAwesomeIcon icon={faPlus} />
            {t('singer.addCustomSong')}
          </button>
        )}
      </div>
    )
  }

  return null
}

export default SearchResults

